import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import * as apps from '../api/apps';
import { useSession } from '../state/useSession';
import { useProjects } from '../state/projects';
import { strings } from '@/theme';
import {
  liveAppRows,
  liveElsewhereRows,
  siteStatusesFrom,
  liveRowLabel,
  MY_APPS_COPY,
  type OwnListing,
} from '@/utils/myApps';
import { Chip, EmptyState, IdentityChip, Screen } from '../ui';

/**
 * Ported from src/app/(main)/(tabs)/my-apps.tsx — the signed-in person's own apps that finished
 * building and are live, by the same shared rule (`liveAppRows`), in the same words.
 *
 * It was the catalogue's list of apps this person had published or JOINED, each opening a
 * listing page rather than the editor, and its empty state sent people to Discover. Rows now come
 * from the person's own projects, each opening its editor; then the person's live apps made on
 * another device (`liveElsewhereRows`), which have no project in this browser and open their page.
 */
export default function MyApps() {
  const navigate = useNavigate();
  const { user } = useSession();
  const projects = useProjects((s) => s.projects);
  const isLoading = useProjects((s) => s.isLoading);
  const error = useProjects((s) => s.error);
  // What the server last said about this person's apps; null until it has answered once.
  const [listings, setListings] = useState<OwnListing[] | null>(null);

  useEffect(() => {
    void useProjects.getState().loadProjects(user?.id ?? '');
  }, [user?.id]);

  /*
   * What the server says about each page. For a project it only ever REMOVES a row it says is
   * offline; an answer that did not come leaves every project row standing on its record, because
   * a failed lookup is a fact about the moment, not about somebody's site — and lists nothing from
   * another device, where the server's word is the only one there is.
   */
  useEffect(() => {
    let live = true;
    void apps
      .fetchMyApps()
      .then((answer) => {
        if (live) setListings(answer);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [user?.id]);

  const rows = useMemo(() => liveAppRows(projects, siteStatusesFrom(listings)), [projects, listings]);
  const elsewhere = useMemo(() => liveElsewhereRows(listings, projects, user?.id), [listings, projects, user?.id]);

  if (rows.length === 0 && elsewhere.length === 0) {
    return (
      <Screen title={strings.tabs.myApps}>
        {isLoading ? (
          <EmptyState icon="emptyApps" title={MY_APPS_COPY.loading} body="" />
        ) : error ? (
          <EmptyState icon="emptyApps" title={MY_APPS_COPY.errorTitle} body={MY_APPS_COPY.errorBody} />
        ) : (
          <EmptyState
            icon="emptyApps"
            title={MY_APPS_COPY.emptyTitle}
            body={MY_APPS_COPY.emptyBody}
            ctaTitle={MY_APPS_COPY.create}
            onCta={() => navigate('/new/idea')}
          />
        )}
      </Screen>
    );
  }

  return (
    <Screen title={strings.tabs.myApps}>
      <ul className="project-list app-grid">
        {rows.map(({ project, url, address, badge }) => (
          <li key={project.id} className="project-item">
            <button
              type="button"
              className="card clay-card pressable project-row"
              onClick={() => navigate(`/project/${project.id}`)}
              aria-label={liveRowLabel({ name: project.name, address, badge })}
            >
              <IdentityChip name={project.name} />
              <span className="project-text">
                <span className="type-heading">{project.name}</span>
                <span className="type-caption project-meta">{address}</span>
              </span>
              {badge && <Chip label={badge} tone="neutral" />}
            </button>
            {/* Beside the row, not inside it: a link inside a button is invalid HTML, and the row
                already opens the editor. The same Open ↗ the Build list has. */}
            <a
              className="project-open"
              href={url}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open ${project.name} in a new tab`}
            >
              Open <span aria-hidden="true">↗</span>
            </a>
          </li>
        ))}
        {/* No project for these in this browser, so no editor: the whole card is one link to the
            page, and says why — rather than a row whose click does something else than its twin. */}
        {elsewhere.map(({ appId, name, url, address, badge }) => (
          <li key={`a:${appId}`} className="project-item">
            <a
              className="card clay-card pressable project-row"
              href={url}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open ${name} in a new tab`}
              aria-describedby={`elsewhere-${appId}`}
            >
              <IdentityChip name={name} />
              <span className="project-text">
                <span className="type-heading">{name}</span>
                <span className="type-caption project-meta">{address}</span>
                <span id={`elsewhere-${appId}`} className="type-caption project-meta">
                  {MY_APPS_COPY.elsewhere}
                </span>
              </span>
              {badge && <Chip label={badge} tone="neutral" />}
              <span aria-hidden="true">↗</span>
            </a>
          </li>
        ))}
      </ul>
    </Screen>
  );
}
